/**
 * Inline X9 brand mark.
 *
 * Replaces the previous `<img src="@x9/brand-assets/logo.svg" />`
 * loading because images don't inherit CSS `color` — the source SVG
 * has no `fill` attribute, so the browser defaults to black. On the
 * marine surfaces every entry / hero screen renders against, a black
 * logo read as wrong against the surrounding white-on-marine type.
 *
 * Rendering the SVG inline (via the path data exposed by
 * `@x9/brand-assets/logo-data`) gives us `fill="currentColor"`, which
 * inherits the parent's `color` and matches the surrounding text by
 * construction. On `text-white` containers (LoadingScreen, ErrorScreen,
 * LandingPage, WelcomeStep, SuccessStep) the logo is now
 * white; on a hypothetical `text-x9-marine` light surface it would
 * automatically render dark.
 *
 * Sizing is driven by the parent's width — we deliberately don't set
 * explicit width/height, only `viewBox`, so the consuming `<div>`'s
 * Tailwind sizing (`w-[min(17vw,4.2rem)]` etc.) governs the display
 * size.
 *
 * Local copy rather than sharing through @x9/brand-assets because
 * brand-assets is currently a pure-JS package without React deps.
 */
import {
  logoPath as logoPathRaw,
  logoViewBox as logoViewBoxRaw,
} from '@x9/brand-assets/logo-data';

const logoPath = logoPathRaw as string;
const logoViewBox = logoViewBoxRaw as string;

interface X9LogoProps {
  className?: string;
  /**
   * When provided, sets `role="img"` + `aria-label` for screen readers.
   * Omit for purely decorative usage (e.g. loading hero, success
   * mark) so AT bypasses the logo.
   */
  title?: string;
}

export default function X9Logo({ className, title }: X9LogoProps) {
  return (
    <svg
      viewBox={logoViewBox}
      className={`fill-current ${className ?? ''}`}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <path d={logoPath} />
    </svg>
  );
}
